import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { fetchUserBps } from 'services/BpService'
import { fetchBpUnits } from 'services/unitsService'
import * as taskService from 'services/taskService'
import { toast } from 'react-toastify'
import SearchableSelect from 'components/SearchableSelect'

const STEPS = [
  { key: 'type', label: 'Select Template' },
  { key: 'building', label: 'Select Building Passport' },
  { key: 'confirm', label: 'Confirm' }
]

const CATEGORY_LABELS = {
  statutory_6m: 'Statutory (6 Monthly)',
  statutory_annual: 'Statutory (Annual)',
  ppm: 'Planned Preventive Maintenance',
  systems: 'Building Systems',
  other: 'Other'
}

const ExpiryWizard = ({ onClose }) => {
  const [step, setStep] = useState(0)
  const [showWizard, setShowWizard] = useState(false)
  const [templates, setTemplates] = useState([])
  const [grouped, setGrouped] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [bps, setBps] = useState([])
  const [bpsLoading, setBpsLoading] = useState(false)
  const [selectedBpId, setSelectedBpId] = useState(null)
  const [units, setUnits] = useState([])
  const [unitsLoading, setUnitsLoading] = useState(false)
  const [selectedUnitIds, setSelectedUnitIds] = useState([])
  const [unitMode, setUnitMode] = useState('shared')
  const [creating, setCreating] = useState(false)
  const [activeAssignments, setActiveAssignments] = useState([])
  const [assignmentsLoading, setAssignmentsLoading] = useState(false)

  // Load maintenance types and active assignments on mount
  useEffect(() => {
    const load = async () => {
      setTemplatesLoading(true)
      try {
        const result = await taskService.getMaintenanceTypes()
        const data = result.data.data
        const tpls = data.templates || []
        setTemplates(tpls)
        setGrouped(data.grouped || [])

        // Load assignments for each template
        setAssignmentsLoading(true)
        const allAssignments = []
        for (const tpl of tpls) {
          try {
            const res = await taskService.getTemplateAssignments(tpl.id)
            const items = res.data?.data || res.data || []
            items.forEach(a => allAssignments.push({ ...a, templateName: tpl.name, templateId: tpl.id }))
          } catch (e) { /* skip */ }
        }
        setActiveAssignments(allAssignments)
        setAssignmentsLoading(false)
      } catch (error) {
        console.error('Failed to load maintenance types:', error)
      } finally {
        setTemplatesLoading(false)
      }
    }
    load()
  }, [])

  // Load BPs when reaching building step
  useEffect(() => {
    if (step !== 1) return
    const loadBps = async () => {
      setBpsLoading(true)
      try {
        const result = await fetchUserBps({ page: 1, sort: 'name', filters: '&filter[task_management_enabled]=1&limit=250' })
        const entries = result?.data?.entries || result?.data?.data || []
        setBps(entries)
      } catch (error) {
        console.error('Failed to load BPs:', error)
      } finally {
        setBpsLoading(false)
      }
    }
    loadBps()
  }, [step])

  // Load units when a building is selected
  useEffect(() => {
    if (!selectedBpId) {
      setUnits([])
      setSelectedUnitIds([])
      return
    }
    const loadUnits = async () => {
      setUnitsLoading(true)
      try {
        const result = await fetchBpUnits({ bpId: selectedBpId })
        const entries = result?.data?.data || result?.data || []
        setUnits(Array.isArray(entries) ? entries : [])
      } catch {
        setUnits([])
      } finally {
        setUnitsLoading(false)
      }
    }
    loadUnits()
    setSelectedUnitIds([])
  }, [selectedBpId])

  // Buildings already assigned for the selected template
  const assignedBpIds = activeAssignments
    .filter(a => a.templateId === selectedTemplate?.id)
    .map(a => a.buildingPassportId)

  const canNext = () => {
    if (step === 0) return !!selectedTemplate
    if (step === 1) return !!selectedBpId
    return true
  }

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1)
  }

  const handleBack = () => {
    if (step > 0) setStep(step - 1)
  }

  const handleConfirm = async () => {
    if (!selectedTemplate || !selectedBpId) return
    setCreating(true)
    try {
      await taskService.createTemplateAssignment({
        templateId: selectedTemplate.id,
        buildingPassportId: selectedBpId,
        unitIds: selectedUnitIds.length > 0 ? selectedUnitIds : undefined,
        unitMode: selectedUnitIds.length > 1 ? unitMode : undefined
      })
      toast.success('Recurring task assignment created successfully')
      if (onClose) onClose()
    } catch (error) {
      toast.error('Failed to create assignment')
      setCreating(false)
    }
  }

  const handleDeleteAssignment = async (assignment) => {
    if (!window.confirm(`Stop recurring "${assignment.templateName}" for this Building Passport?`)) return
    try {
      await taskService.deleteTemplateAssignment({
        templateId: assignment.templateId,
        assignmentId: assignment.id
      })
      setActiveAssignments(prev => prev.filter(a => a.id !== assignment.id))
      toast.success('Recurring assignment stopped')
    } catch (error) {
      toast.error('Failed to stop assignment')
    }
  }

  const selectedBp = bps.find(bp => bp.id === selectedBpId)

  if (!showWizard) {
    return (
      <WizardWrapper>
        <WizardHeader>
          <WizardIcon><EventRepeatIcon /></WizardIcon>
          <div>
            <WizardTitle>Reporting Period</WizardTitle>
            <WizardSubtitle>Manage recurring compliance tasks for your Building Passports</WizardSubtitle>
          </div>
        </WizardHeader>

        <WizardBody>
          <SectionHeader>
            <SectionTitle>Active Recurring Assignments</SectionTitle>
            <SetupBtn type="button" onClick={() => { setShowWizard(true); setStep(0) }}>
              + Set Up New
            </SetupBtn>
          </SectionHeader>

          {assignmentsLoading || templatesLoading ? (
            <LoadingText>Loading assignments...</LoadingText>
          ) : activeAssignments.length === 0 ? (
            <EmptyState>
              <EventRepeatIcon style={{ fontSize: 32 }} />
              <EmptyTitle>No recurring assignments yet</EmptyTitle>
              <EmptyDesc>
                Set up recurring tasks so that inspections and certifications
                are automatically created each period. Fire risk assessments,
                gas safety checks, lift servicing, and more.
              </EmptyDesc>
              <SetupBtn type="button" onClick={() => { setShowWizard(true); setStep(0) }}>
                + Set Up New Assignment
              </SetupBtn>
            </EmptyState>
          ) : (
            <AssignmentList>
              {activeAssignments.map(a => (
                <AssignmentRow key={a.id}>
                  <AssignmentInfo>
                    <AssignmentName>{a.templateName}</AssignmentName>
                    <AssignmentMeta>
                      {a.building?.name || 'Building Passport'}
                      {a.nextDueAt && <> &middot; Next due: {new Date(a.nextDueAt).toLocaleDateString()}</>}
                    </AssignmentMeta>
                  </AssignmentInfo>
                  <StopBtn type="button" onClick={() => handleDeleteAssignment(a)} title="Stop recurring assignment">
                    <DeleteOutlineIcon style={{ fontSize: 18 }} />
                  </StopBtn>
                </AssignmentRow>
              ))}
            </AssignmentList>
          )}
        </WizardBody>
      </WizardWrapper>
    )
  }

  return (
    <WizardWrapper>
      <WizardHeader>
        <WizardIcon><EventRepeatIcon /></WizardIcon>
        <div>
          <WizardTitle>New Recurring Assignment</WizardTitle>
          <WizardSubtitle>Pick a form template and assign it to a Building Passport</WizardSubtitle>
        </div>
      </WizardHeader>

      <StepProgressBar>
        {STEPS.map((s, i) => (
          <ProgressStep key={s.key} $active={i <= step} $current={i === step}>
            <ProgressDot $active={i <= step} $current={i === step}>
              {i < step ? <CheckCircleIcon style={{ fontSize: 16 }} /> : i + 1}
            </ProgressDot>
            <ProgressLabel $active={i <= step}>{s.label}</ProgressLabel>
          </ProgressStep>
        ))}
      </StepProgressBar>

      <WizardBody>
        {step === 0 && (
          <TypeStep>
            {templatesLoading ? (
              <LoadingText>Loading templates...</LoadingText>
            ) : templates.length === 0 ? (
              <EmptyText>
                No published templates with renewal schedules found.
                Create a form template with a renewal interval first.
              </EmptyText>
            ) : (
              grouped.map(group => (
                <TypeGroup key={group.category}>
                  <TypeGroupLabel>
                    {CATEGORY_LABELS[group.category] || group.category}
                  </TypeGroupLabel>
                  <TypeGrid>
                    {group.templates.map(template => (
                      <TypeCard
                        key={template.id}
                        $selected={selectedTemplate?.id === template.id}
                        onClick={() => setSelectedTemplate(template)}
                      >
                        <TypeCardName>{template.name}</TypeCardName>
                        {template.description && (
                          <TypeCardDesc>{template.description}</TypeCardDesc>
                        )}
                        <TypeCardMeta>
                          {template.renewalInterval} {template.renewalUnit}{template.renewalInterval > 1 ? 's' : ''}
                          {' · '}
                          {template.fieldCount} field{template.fieldCount !== 1 ? 's' : ''}
                        </TypeCardMeta>
                      </TypeCard>
                    ))}
                  </TypeGrid>
                </TypeGroup>
              ))
            )}
          </TypeStep>
        )}

        {step === 1 && (
          <BuildingStep>
            {bpsLoading ? (
              <LoadingText>Loading Building Passports...</LoadingText>
            ) : bps.length === 0 ? (
              <EmptyText>No Building Passports with task management enabled found.</EmptyText>
            ) : (
              <>
                <BuildingList>
                  {bps.map(bp => {
                    const alreadyAssigned = assignedBpIds.includes(bp.id)
                    return (
                      <BuildingCard
                        key={bp.id}
                        $selected={selectedBpId === bp.id}
                        $disabled={alreadyAssigned}
                        onClick={() => {
                          if (!alreadyAssigned) setSelectedBpId(bp.id)
                        }}
                      >
                        <BuildingName>{bp.name || bp.address || 'Unnamed Building Passport'}</BuildingName>
                        {bp.address && bp.name && (
                          <BuildingAddress>{bp.address}</BuildingAddress>
                        )}
                        {alreadyAssigned && (
                          <AlreadyAssignedBadge>Already assigned</AlreadyAssignedBadge>
                        )}
                      </BuildingCard>
                    )
                  })}
                </BuildingList>

                {selectedBpId && unitsLoading && (
                  <UnitSection>
                    <UnitLabel>Loading units...</UnitLabel>
                  </UnitSection>
                )}

                {selectedBpId && !unitsLoading && units.length > 0 && (
                  <UnitSection>
                    <UnitLabel>Apply to specific units (optional)</UnitLabel>
                    <SearchableSelect
                      multiple
                      value={selectedUnitIds}
                      onChange={setSelectedUnitIds}
                      options={units.map(unit => ({ value: unit.id, label: unit.name }))}
                      placeholder='Whole Building Passport'
                      searchPlaceholder='Search units…'
                      ariaLabel='Apply to specific units'
                    />
                    <UnitHint>
                      {selectedUnitIds.length > 0
                        ? `${selectedUnitIds.length} unit${selectedUnitIds.length > 1 ? 's' : ''} selected`
                        : 'Hold Ctrl/Cmd to select multiple. Leave empty for the whole Building Passport.'}
                    </UnitHint>

                    {selectedUnitIds.length > 1 && (
                      <UnitModeSection>
                        <UnitLabel>Assignment mode:</UnitLabel>
                        <UnitModeRow>
                          <UnitModeRadio>
                            <input
                              type="radio"
                              id="wizard_unit_mode_shared"
                              name="wizardUnitMode"
                              value="shared"
                              checked={unitMode === 'shared'}
                              onChange={() => setUnitMode('shared')}
                            />
                            <label htmlFor="wizard_unit_mode_shared">Shared task</label>
                          </UnitModeRadio>
                          <UnitModeRadio>
                            <input
                              type="radio"
                              id="wizard_unit_mode_fan_out"
                              name="wizardUnitMode"
                              value="fan_out"
                              checked={unitMode === 'fan_out'}
                              onChange={() => setUnitMode('fan_out')}
                            />
                            <label htmlFor="wizard_unit_mode_fan_out">Separate task per unit</label>
                          </UnitModeRadio>
                        </UnitModeRow>
                        <UnitHint>
                          {unitMode === 'shared'
                            ? 'One task linked to all selected units.'
                            : `Creates ${selectedUnitIds.length} separate tasks, one per unit.`}
                        </UnitHint>
                      </UnitModeSection>
                    )}
                  </UnitSection>
                )}
              </>
            )}
          </BuildingStep>
        )}

        {step === 2 && (
          <ConfirmStep>
            <ConfirmTitle>Review & Confirm</ConfirmTitle>
            <ConfirmCard>
              <ConfirmRow>
                <ConfirmLabel>Template</ConfirmLabel>
                <ConfirmValue>{selectedTemplate?.name}</ConfirmValue>
              </ConfirmRow>
              <ConfirmRow>
                <ConfirmLabel>Renewal</ConfirmLabel>
                <ConfirmValue>
                  Every {selectedTemplate?.renewalInterval} {selectedTemplate?.renewalUnit}
                  {selectedTemplate?.renewalInterval > 1 ? 's' : ''}
                </ConfirmValue>
              </ConfirmRow>
              <ConfirmRow>
                <ConfirmLabel>Building Passport</ConfirmLabel>
                <ConfirmValue>
                  {selectedBp?.name || selectedBp?.address || 'Selected Building Passport'}
                </ConfirmValue>
              </ConfirmRow>
              {selectedUnitIds.length > 0 && (
                <>
                  <ConfirmRow>
                    <ConfirmLabel>Units</ConfirmLabel>
                    <ConfirmValue>
                      {units.filter(u => selectedUnitIds.includes(String(u.id))).map(u => u.name).join(', ')}
                    </ConfirmValue>
                  </ConfirmRow>
                  {selectedUnitIds.length > 1 && (
                    <ConfirmRow>
                      <ConfirmLabel>Mode</ConfirmLabel>
                      <ConfirmValue>
                        {unitMode === 'shared' ? 'Shared task' : `Separate task per unit (${selectedUnitIds.length} tasks)`}
                      </ConfirmValue>
                    </ConfirmRow>
                  )}
                </>
              )}
            </ConfirmCard>
            <ConfirmNote>
              Once confirmed, a new task will be created for this Building Passport every
              {' '}{selectedTemplate?.renewalInterval} {selectedTemplate?.renewalUnit}
              {selectedTemplate?.renewalInterval > 1 ? 's' : ''} using the selected
              template. You can stop the recurrence at any time from this page.
            </ConfirmNote>
          </ConfirmStep>
        )}
      </WizardBody>

      <WizardFooter>
        <BackBtn type="button" onClick={step > 0 ? handleBack : () => setShowWizard(false)}>
          <ArrowBackIcon /> {step > 0 ? 'Back' : 'Cancel'}
        </BackBtn>
        <FooterSpacer />
        {step < STEPS.length - 1 ? (
          <NextBtn type="button" onClick={handleNext} disabled={!canNext()}>
            Next <ArrowForwardIcon />
          </NextBtn>
        ) : (
          <ConfirmBtn type="button" onClick={handleConfirm} disabled={creating}>
            {creating ? 'Creating...' : 'Create Assignment'}
          </ConfirmBtn>
        )}
      </WizardFooter>
    </WizardWrapper>
  )
}

export default ExpiryWizard

const WizardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  background: white;
`

const WizardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 0;
`

const WizardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { font-size: 24px; }
`

const WizardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`

const WizardSubtitle = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 2px 0 0;
`

const StepProgressBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 20px 24px 0;

  @media (max-width: 480px) {
    gap: 2px;
    padding: 16px 12px 0;
  }
`

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: calc(-50% + 12px);
    right: calc(50% + 12px);
    height: 2px;
    background: ${({ $active, theme }) => $active ? theme.colors.secondary : theme.colors.lightGrey};
  }

  &:first-child::before { display: none; }
`

const ProgressDot = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $active, theme }) => $active ? theme.colors.secondary : theme.colors.lightGrey};
  color: ${({ $active, theme }) => $active ? 'white' : (theme.colors.grey?.[800] || '#999')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  z-index: 1;
`

const ProgressLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ $active, theme }) => $active ? theme.colors.haiti : (theme.colors.grey?.[800] || '#999')};
  white-space: nowrap;
`

const WizardBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;

  @media (max-width: 480px) {
    padding: 16px;
  }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
`

const SetupBtn = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  svg { margin-bottom: 12px; }
`

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const EmptyDesc = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  max-width: 400px;
  line-height: 1.5;
  margin: 0 0 20px;
`

const AssignmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AssignmentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  gap: 12px;
`

const AssignmentInfo = styled.div`
  min-width: 0;
  flex: 1;
`

const AssignmentName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AssignmentMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const StopBtn = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  flex-shrink: 0;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
    border-color: ${({ theme }) => theme.colors.error};
  }
`

const TypeStep = styled.div``

const TypeGroup = styled.div`
  margin-bottom: 24px;
  &:last-child { margin-bottom: 0; }
`

const TypeGroupLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 10px;
`

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const TypeCard = styled.div`
  padding: 14px;
  border: 2px solid ${({ $selected, theme }) => $selected ? theme.colors.secondary : theme.colors.lightGrey};
  border-radius: 10px;
  cursor: pointer;
  background: ${({ $selected, theme }) => $selected ? `${theme.colors.secondary}08` : 'white'};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ $selected, theme }) => $selected ? theme.colors.secondary : (theme.colors.grey?.[800] || '#cbd5e1')};
  }
`

const TypeCardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
`

const TypeCardDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 6px;
`

const TypeCardMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 500;
`

const BuildingStep = styled.div``

const BuildingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BuildingCard = styled.div`
  padding: 14px 16px;
  border: 2px solid ${({ $selected, $disabled, theme }) =>
    $disabled ? theme.colors.lightGrey : $selected ? theme.colors.secondary : theme.colors.lightGrey};
  border-radius: 10px;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  background: ${({ $selected, $disabled, theme }) =>
    $disabled ? `${theme.colors.lightGrey}` : $selected ? `${theme.colors.secondary}08` : 'white'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  transition: all 0.15s;

  &:hover {
    border-color: ${({ $selected, $disabled, theme }) =>
      $disabled ? theme.colors.lightGrey : $selected ? theme.colors.secondary : (theme.colors.grey?.[800] || '#cbd5e1')};
  }
`

const BuildingName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const BuildingAddress = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const AlreadyAssignedBadge = styled.div`
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 4px;
  padding: 2px 8px;
  margin-top: 4px;
`

const UnitModeSection = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const UnitModeRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 6px;
`

const UnitModeRadio = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;

  input[type="radio"] {
    accent-color: ${({ theme }) => theme.colors.secondary};
  }
`

const UnitSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
`

const UnitLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 10px;
`

const UnitHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 8px;
`

const ConfirmStep = styled.div`
  max-width: 400px;
  margin: 0 auto;
`

const ConfirmTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 16px;
  text-align: center;
`

const ConfirmCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
`

const ConfirmRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};

  &:last-child { border-bottom: none; }
`

const ConfirmLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  font-weight: 500;
`

const ConfirmValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: right;
`

const ConfirmNote = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  text-align: center;
  line-height: 1.5;
  margin: 0;
`

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const EmptyText = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  line-height: 1.5;
`

const WizardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};

  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`

const FooterSpacer = styled.div`
  flex: 1;
`

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  svg { font-size: 18px; }
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`


const NextBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  svg { font-size: 18px; }
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const ConfirmBtn = styled.button`
  padding: 10px 20px;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
